'use client';
import { cn } from '@bells/core/lib/utils';
import { layoutNavigationHome, layoutNavigationMore } from '@bells/i18n';
import type { Locale } from '@bells/i18n/runtime';
import { NavigationType } from '@bells/schema/models/navigation';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@bells/ui/components/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@bells/ui/components/dropdown-menu';
import { InlineSVG } from '@bells/ui/components/inline-svg';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@bells/ui/components/sidebar';
import type { NavigationItemTransform } from '@bells/ui/utils/navigation';
import { slice } from 'lodash';
import { ChevronRight, Home, MoreHorizontal } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { memo, useMemo } from 'react';

// ✅ NavItem: Memoized
export const NavItem = memo(({ item }: { item: NavigationItemTransform }) => {
  const pathname = usePathname();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={pathname.startsWith(item.url ?? '')}>
        <Link prefetch={false} href={item.url ?? ''}>
          {item.svgIcon && <InlineSVG className='h-4 w-4' src={item.svgIcon} />}
          <span>{item.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
});
NavItem.displayName = 'NavItem';

// ✅ NavSubGroup: useMemo für Chunking
export const NavSubGroup = memo(
  ({
    group,
    moreText,
    chunkSize = 10000,
  }: {
    group: NavigationItemTransform;
    chunkSize?: number;
    moreText: string;
  }) => {
    const pathname = usePathname();
    const router = useRouter();
    const { state } = useSidebar();

    const { main, chunked } = useMemo(() => {
      return {
        main: slice(group.childs, 0, chunkSize),
        chunked: slice(group.childs, chunkSize),
      };
    }, [group]);

    return (
      <Collapsible asChild defaultOpen={false} className='group/collapsible'>
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              tooltip={group.title}
              onClick={() => {
                if (state !== 'expanded' && group.url) {
                  router.push(group.url);
                }
              }}
            >
              {group.svgIcon && (
                <InlineSVG className='h-4 w-4' src={group.svgIcon} />
              )}
              <span>{group.title}</span>
              <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent className='data-[state=closed]:hidden' forceMount>
            <SidebarMenuSub>
              {main.map((subItem) => (
                <SidebarMenuSubItem key={subItem.title}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={
                      subItem.url && pathname.startsWith(subItem.url)
                        ? true
                        : false
                    }
                  >
                    <Link prefetch={false} href={subItem.url ?? ''}>
                      {subItem.title}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
              {chunked.length > 0 && (
                <DropdownMenu>
                  <SidebarMenuItem>
                    <DropdownMenuTrigger asChild>
                      <SidebarMenuButton className='w-full data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'>
                        {moreText} <MoreHorizontal className='ml-auto' />
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      side={'right'}
                      align={'start'}
                      className='min-w-56 rounded-lg'
                    >
                      {chunked.map((item) => (
                        <DropdownMenuItem asChild key={item.title}>
                          <Link
                            prefetch={false}
                            className='cursor-pointer'
                            href={item.url ?? ''}
                          >
                            {item.title}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </SidebarMenuItem>
                </DropdownMenu>
              )}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }
);
NavSubGroup.displayName = 'NavSubGroup';

// ✅ NavGroup: Memoized
export const NavGroup = memo(
  ({ group }: { group: NavigationItemTransform }) => {
    return (
      <SidebarGroup className='w-full'>
        <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
        <SidebarMenu className='w-full'>
          {group.childs.map((item) =>
            item.type === NavigationType.SUBGROUP ? (
              <NavSubGroup
                group={item}
                key={item.id}
                moreText={layoutNavigationMore()}
              />
            ) : (
              <NavItem item={item} key={item.id} />
            )
          )}
        </SidebarMenu>
      </SidebarGroup>
    );
  }
);
NavGroup.displayName = 'NavGroup';

// ✅ NavMain: useMemo für Gruppen
export const NavMain = memo(
  ({ items, locale }: { items: NavigationItemTransform[]; locale: Locale }) => {
    const pathname = usePathname();

    const navGroups = useMemo(() => {
      return items
        .filter((df) => !df.parentId)
        .map((group) => <NavGroup group={group} key={`group-${group.id}`} />);
    }, [items]);

    return (
      <nav className='w-full'>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={pathname == (locale === 'de' ? '/' : `/${locale}`)}
              >
                <Link
                  prefetch={false}
                  href={locale === 'de' ? '/' : `/${locale}`}
                >
                  <Home />
                  <span>{layoutNavigationHome(locale)}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        {navGroups}
      </nav>
    );
  }
);
NavMain.displayName = 'NavMain';
